import { Capacitor } from "@capacitor/core";
import { Media } from "@capacitor-community/media";

const APP_ALBUM_NAME = "MeeMarriage";

const blobToDataUrl = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      if (typeof reader.result === "string") {
        resolve(reader.result);
        return;
      }
      reject(new Error("Unable to read downloaded image."));
    };
    reader.onerror = () => reject(new Error("Unable to read downloaded image."));
    reader.readAsDataURL(blob);
  });

const normalizeFileName = (value: string) =>
  value
    .replace(/\.[^.]+$/, "")
    .replace(/[^\w.-]+/g, "_")
    .replace(/^_+|_+$/g, "") || "image";

let cachedAlbumIdentifier: string | null = null;

const resolveAlbumIdentifier = async (): Promise<string | undefined> => {
  if (Capacitor.getPlatform() !== "android") {
    return undefined;
  }

  if (cachedAlbumIdentifier) {
    return cachedAlbumIdentifier;
  }

  try {
    const [{ albums }, { path: albumsPath }] = await Promise.all([
      Media.getAlbums(),
      Media.getAlbumsPath(),
    ]);

    const existingAlbum = albums.find(
      (album) =>
        album.name === APP_ALBUM_NAME &&
        (!albumsPath || album.identifier.startsWith(`${albumsPath}/${APP_ALBUM_NAME}`)),
    );

    if (existingAlbum?.identifier) {
      cachedAlbumIdentifier = existingAlbum.identifier;
      return cachedAlbumIdentifier;
    }
  } catch {
    // Fall through and try creating the album.
  }

  await Media.createAlbum({ name: APP_ALBUM_NAME });

  const refreshedAlbums = await Media.getAlbums();
  const createdAlbum = refreshedAlbums.albums.find(
    (album) => album.name === APP_ALBUM_NAME,
  );

  if (!createdAlbum?.identifier) {
    throw new Error("Unable to access app gallery album.");
  }

  cachedAlbumIdentifier = createdAlbum.identifier;
  return cachedAlbumIdentifier;
};

export const saveImageToGallery = async (
  blob: Blob,
  fileName: string,
): Promise<void> => {
  const dataUrl = await blobToDataUrl(blob);
  const albumIdentifier = await resolveAlbumIdentifier();

  await Media.savePhoto({
    path: dataUrl,
    albumIdentifier,
    fileName: normalizeFileName(fileName),
  });
};

